import { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { m, AnimatePresence } from "framer-motion";
import { useI18n } from "@/contexts/I18nContext";
import { DASHBOARD_URL } from "@/lib/supabase";
import LanguageSwitcher from "./LanguageSwitcher";
import ThemeSwitcher from "./ThemeSwitcher";

interface SharedNavbarProps {
  activePage?: string;
}

const NAV_LINKS = [
  { page: "funzionalita", labelKey: "site.nav.features", to: "/funzionalita" },
  { page: "simulatore", labelKey: "site.nav.simulator", to: "/simulatore" },
  { page: "prezzi", labelKey: "site.nav.pricing", to: "/prezzi" },
  { page: "confronta", labelKey: "site.nav.compare", to: "/vs-fiduciario-tradizionale" },
  { page: "cantoni", labelKey: "site.nav.cantoni", to: "/confronta-cantoni" },
  { page: "faq", labelKey: "site.nav.faq", to: "/faq" },
  { page: "blog", labelKey: "site.nav.blog", to: "/blog" },
  { page: "fiduciari", labelKey: "site.nav.fiduciari", to: "/fiduciari" },
  { page: "frontalieri", labelKey: "site.nav.frontalieri", to: "/frontalieri" },
];

const LOGIN_HREF = DASHBOARD_URL ? `${DASHBOARD_URL}/` : "/login";

// Prefetch on hover: stessa `import()` della React.lazy in App.tsx riscalda il module cache.
const ROUTE_PREFETCH: Record<string, () => Promise<unknown>> = {
  "/funzionalita": () => import("@/pages/Funzionalita"),
  "/simulatore": () => import("@/pages/Simulatore"),
  "/prezzi": () => import("@/pages/Prezzi"),
  "/vs-fiduciario-tradizionale": () => import("@/pages/VsFiduciarioTradizionale"),
  "/confronta-cantoni": () => import("@/pages/ConfrontoCantoni"),
  "/faq": () => import("@/pages/FAQ"),
  "/blog": () => import("@/pages/Blog"),
  "/fiduciari": () => import("@/pages/Fiduciari"),
  "/frontalieri": () => import("@/pages/Frontalieri"),
  "/registrazione": () => import("@/pages/Registrazione"),
};

const prefetched = new Set<string>();
function prefetchRoute(path: string) {
  if (prefetched.has(path)) return;
  const loader = ROUTE_PREFETCH[path];
  if (!loader) return;
  prefetched.add(path);
  void loader();
}

export default function SharedNavbar({ activePage }: SharedNavbarProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { t } = useI18n();

  const linkClass = (page: string) =>
    `text-sm font-medium transition-colors duration-300 ${
      activePage === page ? "text-foreground" : "text-muted-foreground hover:text-foreground"
    }`;

  const onHover = useCallback((to: string) => () => prefetchRoute(to), []);

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 flex items-center justify-center px-4 md:px-8 py-4 bg-background/60 backdrop-blur-2xl border-b border-border/30">
        <div className="max-w-[1200px] w-full flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <picture>
              <source srcSet="/logo-knot-lg.avif" type="image/avif" />
              <source srcSet="/logo-knot-lg.webp" type="image/webp" />
              <img src="/logo-knot-lg.png" alt="Swissez" width={36} height={36} className="h-9 w-auto" />
            </picture>
            <span className="text-xl font-bold tracking-tight text-foreground">
              swiss<span className="text-primary">ez</span>
            </span>
          </Link>

          {/* Desktop nav */}
          <div className="hidden lg:flex items-center gap-7">
            {NAV_LINKS.map((l) => (
              <Link
                key={l.page}
                to={l.to}
                onMouseEnter={onHover(l.to)}
                onFocus={onHover(l.to)}
                className={linkClass(l.page)}
              >
                {t(l.labelKey)}
              </Link>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <ThemeSwitcher />
            <LanguageSwitcher />
            <a
              href={LOGIN_HREF}
              rel="noopener"
              className="hidden md:inline-flex text-sm font-semibold text-foreground border border-border/60 rounded-full px-5 py-2 hover:border-primary hover:text-primary transition-all duration-300"
            >
              {t("site.nav.login")}
            </a>
            <Link
              to="/registrazione"
              onMouseEnter={onHover("/registrazione")}
              onFocus={onHover("/registrazione")}
              className="hidden sm:inline-flex bg-primary text-primary-foreground rounded-full px-6 py-2.5 text-sm font-semibold hover:shadow-lg hover:shadow-primary/25 transition-all duration-300"
            >
              {t("site.nav.cta")}
            </Link>

            {/* Hamburger */}
            <button
              type="button"
              onClick={() => setMobileOpen(!mobileOpen)}
              className="lg:hidden flex items-center justify-center w-10 h-10 rounded-xl border border-border/40 text-foreground hover:border-primary transition-colors"
              aria-label={t("site.a11y.menu")}
              aria-expanded={mobileOpen}
              aria-controls="mobile-nav-panel"
            >
              {mobileOpen ? <X className="w-5 h-5" aria-hidden="true" /> : <Menu className="w-5 h-5" aria-hidden="true" />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            {/* Backdrop */}
            <m.div
              className="fixed inset-0 bg-background/80 backdrop-blur-sm z-40 lg:hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMobileOpen(false)}
            />

            {/* Panel */}
            <m.div
              id="mobile-nav-panel"
              role="dialog"
              aria-modal="true"
              aria-label={t("site.a11y.menu")}
              className="fixed top-[65px] right-0 bottom-0 w-[280px] bg-background border-l border-border/30 z-50 lg:hidden overflow-y-auto"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
            >
              <div className="flex flex-col p-6 gap-1">
                {NAV_LINKS.map((l, i) => (
                  <m.div
                    key={l.page}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.05 }}
                  >
                    <Link
                      to={l.to}
                      onClick={() => setMobileOpen(false)}
                      className={`block py-3 px-4 rounded-xl text-base font-medium transition-all duration-200 ${
                        activePage === l.page
                          ? "text-primary bg-primary/10"
                          : "text-foreground hover:text-primary hover:bg-primary/5"
                      }`}
                    >
                      {t(l.labelKey)}
                    </Link>
                  </m.div>
                ))}

                <div className="h-px bg-border/30 my-4" />

                <a
                  href={LOGIN_HREF}
                  rel="noopener"
                  onClick={() => setMobileOpen(false)}
                  className="block py-3 px-4 rounded-xl text-base font-medium text-foreground hover:text-primary hover:bg-primary/5 transition-all"
                >
                  {t("site.nav.login")}
                </a>

                <Link
                  to="/registrazione"
                  onClick={() => setMobileOpen(false)}
                  className="mt-2 block text-center bg-primary text-primary-foreground rounded-full py-3.5 text-sm font-semibold hover:shadow-lg hover:shadow-primary/25 transition-all"
                >
                  {t("site.nav.cta")}
                </Link>
              </div>
            </m.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
